/** Module alias with the corresponding init function */
export type ModuleIndex = {
  [key: string]: () => void | undefined;
};

/**
 * Checks for all modules in use on the page and tries to inialize them.
 * @param modules All available modules with their respective alias
 */
export const initializeModules = (modules: ModuleIndex) => {
  if (!modules || Object.keys(modules).length === 0) {
    throw "No script modules registered in index.ts";
  }

  const modulesInUse = getModulesToInitialize();
  modulesInUse.forEach(moduleAlias => {
    const initFunction = modules[moduleAlias];
    if (initFunction) {
      console.info(`[Initialize module] ${moduleAlias}`);
      initModule(initFunction);
    } else {
      console.error(
        `Cannot initialize unknown module "${moduleAlias}". Make sure it's added in index.ts`
      );
    }
  });
};

/**
 * Scans the page for elements with the data-require attribute
 * and picks script module names from the value (comma separated)
 */
const getModulesToInitialize = (): string[] => {
  const uniqueModules = new Set<string>();
  document.querySelectorAll("[data-require]").forEach(elem => {
    const requireValue = (elem as HTMLElement)?.dataset.require;
    const modules = requireValue?.split(",").map(module => module.trim()) ?? [];
    modules.forEach(module => {
      if (module && !uniqueModules.has(module)) {
        uniqueModules.add(module);
      }
    });
  });

  return [...uniqueModules];
};

/**
 * Runs the init method for a module and logs an error
 * to the console and continues the code if the module
 * initialization fails.
 * @param callback a module init method (entry point of a module)
 */
const initModule = (callback: () => void) => {
  try {
    callback();
  } catch (err) {
    console.error("Failed to initialize module", err);
  }
};
